import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './App.css';
import { BOTS } from './config';

// --- Helpers ---
const getBackendUrl = () => {
  return window.env?.BACKEND_URL || 'http://localhost:3001';
};

const BASE_BACKEND_URL = getBackendUrl();
console.log('Backend URL:', BASE_BACKEND_URL);

// --- Components ---

// FileUpload Component (Simplified)
function FileUpload({ onFileSelect, selectedFile, onRemoveFile }) {
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            onFileSelect(e.target.files[0]);
        }
    };

    const openFileDialog = () => fileInputRef.current.click();

    return (
        <div className="file-upload-container">
            <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
                accept="image/*"
            />
            {selectedFile ? (
                <div className="file-preview-compact">
                    <span title={selectedFile.name}>📎 {selectedFile.name}</span>
                    <button onClick={onRemoveFile} className="remove-file-btn-compact">×</button>
                </div>
            ) : (
                <button onClick={openFileDialog} className="upload-icon-btn">
                    <span>🔗</span>
                </button>
            )}
        </div>
    );
}


// ChatInterface Component
function ChatInterface({ bot, onBack, sessionId, selectedFile, setSelectedFile, displayName }) {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        // Set initial greeting message once displayName is available
        if (displayName) {
            setMessages([{ 
                id: 'initial', 
                text: `Chào ${displayName}! Tôi là AI QTM.`, 
                sender: 'bot' 
            }]);
        }
    }, [displayName]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!inputValue.trim() && !selectedFile) return;

        const formData = new FormData();
        formData.append('message', inputValue);
        formData.append('sessionId', sessionId);
        formData.append('displayName', displayName);
        if (selectedFile) {
            formData.append('file', selectedFile, selectedFile.name);
        }

        const userMessage = {
            id: Date.now(),
            text: inputValue,
            sender: 'user',
            fileName: selectedFile ? selectedFile.name : null
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setSelectedFile(null); // Clear file after sending
        setIsLoading(true);

        try {
            const response = await fetch(`${BASE_BACKEND_URL}${bot.endpoint}`, {
                method: 'POST',
                body: formData,
                credentials: 'include' // <--- Send cookies with the request
            });
            if (!response.ok) throw new Error((await response.json()).error || 'Lỗi mạng');
            
            const data = await response.json();
            console.log('--- Backend Response ---');
            console.log('Data:', JSON.stringify(data, null, 2));
            console.log('--------------------------');

            const replyText = data && data[0] && data[0].output;
            if (replyText) {
                setMessages(prev => [...prev, { id: Date.now() + 1, text: replyText, sender: 'bot' }]);
            } else {
                throw new Error("Invalid response format from server");
            }
        } catch (error) {
            setMessages(prev => [...prev, { id: Date.now() + 1, text: `Lỗi: ${error.message}`, sender: 'bot', isError: true }]);
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="chat-app">
            <header className="chat-header">
                <button onClick={onBack} className="back-button">←</button>
                <img src={bot.logo} alt={`${bot.name} Logo`} className="header-logo" />
                <h1>{bot.name}</h1>
            </header>
            <div className="chat-messages">
                {messages.map((msg) => (
                    <div key={msg.id} className={`message-row ${msg.sender === 'user' ? 'user-row' : 'bot-row'}`}>
                        <div className={`message-bubble ${msg.sender === 'user' ? 'user-message' : 'bot-message'} ${msg.isError ? 'error-message' : ''}`}>
                            {msg.sender === 'bot' && !msg.isError ? <ReactMarkdown components={{
                                img: ({node, ...props}) => <img style={{maxWidth: '100%', height: 'auto'}} {...props} />
                            }}>{msg.text}</ReactMarkdown> : <p>{msg.text}</p>}
                            {msg.fileName && <div className="message-file-attachment">📎 {msg.fileName}</div>}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
            {isLoading && <div className="loading-indicator">Bot đang soạn tin...</div>}
            <form className="chat-input-form" onSubmit={handleSendMessage}>
                 <FileUpload 
                    onFileSelect={setSelectedFile} 
                    selectedFile={selectedFile}
                    onRemoveFile={() => setSelectedFile(null)}
                />
                <div className="input-wrapper">
                    <textarea 
                        ref={inputRef} 
                        value={inputValue} 
                        onChange={(e) => setInputValue(e.target.value)} 
                        onKeyDown={handleKeyDown}
                        placeholder="Nhập câu hỏi hoặc dán ảnh..." 
                        disabled={isLoading}
                        className={selectedFile ? 'input-with-file' : ''}
                        rows={1}
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                    />
                </div>
                <button type="submit" disabled={isLoading || (!inputValue.trim() && !selectedFile)}>{isLoading ? '...' : 'Gửi'}</button>
            </form>
        </div>
    );
}

export default function ChatPage() {
    const [activeBotKey, setActiveBotKey] = useState(null);
    const [sessionInfo, setSessionInfo] = useState({ user: null, sessionId: null });
    const [backgroundUrl, setBackgroundUrl] = useState(localStorage.getItem('backgroundUrl') || '');
    const [showSettings, setShowSettings] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        document.title = "Cổng Chatbot QTM";

        const fetchSession = async () => {
            try {
                const response = await fetch(`${BASE_BACKEND_URL}/auth/session`, {
                    credentials: 'include',
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch session');
                }
                const data = await response.json();
                setSessionInfo(data);
                console.log('Session info fetched:', data);
            } catch (error) {
                console.error('Error fetching session:', error);
                // Handle error, maybe redirect to login
            }
        };

        fetchSession();
    }, []);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (activeBotKey) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (!activeBotKey) return;

        const items = e.dataTransfer.items;
        const imageFile = Array.from(e.dataTransfer.files).find(file => file.type.startsWith('image/'));
        if (imageFile) {
            setSelectedFile(imageFile);
            return;
        }
        
        alert('Only image files are accepted!');
    };
    
    const handleGlobalPaste = (e) => {
        if (!activeBotKey) return;
        const items = e.clipboardData.items;
        const imageFile = Array.from(items).find(item => item.type.startsWith('image/'));
        if (imageFile) {
            const blob = imageFile.getAsFile();
            const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
            setSelectedFile(file);
            e.preventDefault();
        }
    };

    useEffect(() => {
        window.addEventListener('paste', handleGlobalPaste);
        return () => {
            window.removeEventListener('paste', handleGlobalPaste);
        };
    }, [activeBotKey]);

    const saveBackground = () => {
        localStorage.setItem('backgroundUrl', backgroundUrl);
        setShowSettings(false);
    };

    return (
        <div 
            className={`app-container ${isDragging ? 'drag-over-active' : ''}`}
            style={{ backgroundImage: `url(${backgroundUrl})` }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="settings-icon" onClick={() => setShowSettings(true)}>⚙️</div>
            
            {showSettings && (
                <div className="settings-modal">
                    <div className="settings-content">
                        <h3>Cài đặt giao diện</h3>
                        <label>URL ảnh nền:</label>
                        <input type="text" value={backgroundUrl} onChange={(e) => setBackgroundUrl(e.target.value)} placeholder="https://..." />
                        <div className="settings-buttons">
                           <button onClick={saveBackground}>Lưu</button>
                           <button onClick={() => setShowSettings(false)}>Đóng</button>
                        </div>
                    </div>
                </div>
            )}
            
            {!activeBotKey ? (
                <div className="bot-selection-container">
                    <h2>Chọn Chatbot</h2>
                    <div className="bot-options">
                        {Object.keys(BOTS).map(key => (
                            <div key={key} className="bot-option" onClick={() => setActiveBotKey(key)}>
                                <img src={BOTS[key].logo} alt={`${BOTS[key].name} Logo`} />
                                <h3>{BOTS[key].name}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="chat-view-container">
                    {sessionInfo.user && (
                        <header className="main-header">
                            Logged in as: {sessionInfo.user.displayName}
                        </header>
                    )}
                    <ChatInterface 
                        bot={BOTS[activeBotKey]} 
                        onBack={() => {
                            setActiveBotKey(null);
                            setSelectedFile(null);
                        }} 
                        sessionId={sessionInfo.sessionId}
                        selectedFile={selectedFile}
                        setSelectedFile={setSelectedFile}
                        displayName={sessionInfo.user?.displayName}
                    />
                </div>
            )}
             {isDragging && <div className="drag-drop-overlay">Kéo và thả file ảnh vào đây</div>}
        </div>
    );
}
